import { NavigationGuardNext, Route } from "vue-router"
import { session } from "@/auth/session"
import { raiseError } from "@/utils/event-bus"
import { translate } from "@/plugins/i18n"
import { validateLicense } from "@/utils/event-bus"
import { PlatformViewType } from "@/types/core"
import { useStore } from "@/composable/useStore"
import { OrganizationType } from "@/types/organization"
import { UserRoleEnum } from "@/types/user"
import { DefaultRoute } from "@/router/lib/default-routes"
import { useWebview } from "@/composable/useWebview"

const sendTo = (name: string, to: Route, next: NavigationGuardNext<Vue>) => {
  if (to.name !== name) {
    return next({ name })
  }
}

const _updateTitle = (route: Route) => {
  if (route.meta?.title) {
    document.title = `Viz Now - ${route.meta.title}`
  } else {
    document.title = `Viz Now`
  }
}

export const authGuard = (to: Route, _: Route, next: NavigationGuardNext<Vue>) => {
  const { SysAdmin, Admin } = UserRoleEnum
  const isLoggedIn = session.auth.isAuthenticated
  const needsToBeLoggedIn = to.meta?.requiresAuth ?? true
  const isInternalTool = to.meta?.isInternalTool ?? false
  const requiresAdminAccess = to.meta?.requireAdmin ?? false

  if (!session.auth.initiated) {
    return next()
  }

  if (isLoggedIn) {
    validateLicense()
  }

  _updateTitle(to)

  if (isLoggedIn && !needsToBeLoggedIn) {
    return sendTo(DefaultRoute.ROOT, to, next)
  } else if (!isLoggedIn && needsToBeLoggedIn) {
    raiseError({ text: translate("common.notAuthorized") })
    return sendTo(DefaultRoute.LOGIN, to, next)
  }

  if (isInternalTool) {
    if (!canJoinRoleProtectedRoute(SysAdmin)) {
      return sendTo(DefaultRoute.ROOT, to, next)
    }
  }

  if (requiresAdminAccess) {
    if (!canJoinRoleProtectedRoute(Admin)) {
      return sendTo(DefaultRoute.ROOT, to, next)
    }
  }

  if (appLauncherRestricted(to?.meta?.type)) {
    return sendTo(DefaultRoute.SAAS_APP_LAUNCHER, to, next)
  }

  if (unauthorizedRouteType(to?.meta?.type)) {
    return sendTo(DefaultRoute.ROOT, to, next)
  }

  return next()
}

export const canJoinRoleProtectedRoute = (requiredRole: UserRoleEnum) => {
  const { SysAdmin, Admin, Support } = UserRoleEnum
  const user = session.auth

  switch (requiredRole) {
    case SysAdmin:
    case Support:
      return user.isSysAdmin || user.isSupport

    case Admin:
      return user.isSysAdmin || user.isSupport || user.isAdmin

    default:
      return true
  }
}

const appLauncherRestricted = (routeType: PlatformViewType | undefined) => {
  const { appLauncherDetected } = useWebview()
  const { VIZ_NOW } = PlatformViewType

  if (appLauncherDetected.value) {
    if (routeType === VIZ_NOW) {
      return true
    }
  }

  return false
}

const unauthorizedRouteType = (routeType: PlatformViewType | undefined) => {
  try {
    const { APP_LAUNCHER, BOTH, VIZ_NOW } = PlatformViewType
    const { usersModule, organizationModule } = useStore()

    if (usersModule.selfDetail && routeType) {
      const me = usersModule.selfDetail
      const superRole = [UserRoleEnum.SysAdmin, UserRoleEnum.Support]
      const userOrganizationIsSaaS = organizationModule.organizationDetail?.organizationType === OrganizationType.SaaS

      if (superRole.includes(me.role)) {
        return false
      }

      if (userOrganizationIsSaaS) {
        const allowedRoutesBySaaS = [APP_LAUNCHER, BOTH]
        return !allowedRoutesBySaaS.includes(routeType)
      } else {
        const allowedRoutesByCustomerHosted = [VIZ_NOW, BOTH]
        return !allowedRoutesByCustomerHosted.includes(routeType)
      }
    }

    return false
  } catch {
    return false
  }
}
